/**
 * src/features/sync/lib/hydrate.ts
 *
 * SYNC-03 (Phase 36 plan 05) — the missing client caller for the two hydration
 * endpoints that already exist server-side: GET /api/projects (index) and
 * GET /api/projects/:id (detail; `project.data` is re-parsed through the
 * Phase-35-fixed ProjectSnapshotSchema, so the global item layer comes back
 * intact). Both are user_id-scoped (T-04-04, T-03-11) — this module adds no id
 * the client did not receive from its own index.
 *
 * ORDERING IS THE PHASE'S NAMED BLAST-RADIUS RISK (STATE.md): a pre-Phase-35
 * server row can return `projectItems: []` (the schema default applied over
 * stripped stored JSON). Hydrating before the local queue has flushed could
 * read that stripped snapshot down and overwrite the intact local copy that is
 * the ONLY source for repairing it. `syncAndHydrate()` below therefore awaits
 * `flush()` to full resolution before issuing the first GET — never
 * `Promise.all` (see mutation proof #3, test/features/sync/hydrate.test.ts).
 *
 * Hydration populates the library list ONLY — it never mutates which project is
 * currently open, never triggers a full open-estimate reload, and never touches
 * the in-memory project-editor state at all. Silently swapping the open estimate
 * under a working user is a data-trust failure even when no bytes are lost
 * (CLAUDE.md rules 12/13).
 */
import { peekQueue } from '@/shared/lib/changeQueue';
import { getProjectLibrary, saveProjectLibrary } from '@/entities/estimate/lib/project';
import { mergeProjectIntoLibrary } from '@/entities/estimate/lib/hooks';
import { useSyncStore } from '@/stores/useSyncStore';
import { ensureFirstSyncUploaded } from './firstSync';
import {
  ProjectsIndexResponseSchema,
  ProjectDetailResponseSchema,
} from '@/shared/validation/schemas';
import type { ProjectsIndexResponse } from '@/shared/validation/schemas';
import type { ProjectSnapshot } from '@/entities/estimate/model/types';

/**
 * Mirrors migrateLocalToCloud's timeout discipline (src/stores/migrate.ts) — a hung
 * Worker cold-start must not block hydration for the whole login session.
 */
const HYDRATE_TIMEOUT_MS = 10_000;

/**
 * Reads the account's projects (index + per-project detail) into the local library.
 * Never throws — every failure path sets useSyncStore's error state and returns,
 * leaving the local library exactly as it was (the user's capital, CLAUDE.md
 * rules 12/13; UI-SPEC backstop row 9).
 */
export async function hydrateProjectsFromServer(): Promise<void> {
  useSyncStore.getState().setHydrating(true);
  try {
    let projects: ProjectsIndexResponse['projects'];

    try {
      const indexRes = await fetch('/api/projects', {
        method: 'GET',
        signal: AbortSignal.timeout(HYDRATE_TIMEOUT_MS),
      });
      if (!indexRes.ok) {
        // Unknown server state is never a reason to modify the local library —
        // error out and return without touching it (UI-SPEC backstop row 9).
        useSyncStore.getState().setError('Hydration failed: could not read the project list');
        return;
      }
      projects = ProjectsIndexResponseSchema.parse(await indexRes.json()).projects;
    } catch {
      // Network throw OR a response that fails ProjectsIndexResponseSchema — same
      // safe-default: leave the library exactly as it was.
      useSyncStore.getState().setError('Hydration failed: could not read the project list');
      return;
    }

    if (projects.length === 0) {
      // Zero server projects — no detail GETs, no writes. Zero/one/many share this
      // one code path; no count-specific branch (UI-SPEC row 13).
      return;
    }

    // Belt-and-braces on top of the flush-first ordering in syncAndHydrate() below:
    // if flush() itself left a record behind (the server rejected it), that project
    // must still not be overwritten by whatever the server currently holds.
    const queued = await peekQueue();
    const skipIds = new Set(queued.map((r) => r.projectId));

    // listProjects returns newest-first (ORDER BY last_modified DESC); the merge
    // helper below prepends each incoming snapshot. Merging in REVERSE order means
    // the newest project is merged LAST, landing at the head of the library —
    // matching the server's ordering rather than inverting it (UI-SPEC ordering
    // backstop).
    for (let i = projects.length - 1; i >= 0; i--) {
      const entry = projects[i];
      const id = String(entry.id);
      if (skipIds.has(id)) continue;

      try {
        const detailRes = await fetch(`/api/projects/${entry.id}`, {
          method: 'GET',
          signal: AbortSignal.timeout(HYDRATE_TIMEOUT_MS),
        });
        if (!detailRes.ok) {
          // 404 (or any other non-2xx) — skip this one project, never synthesize a
          // partial row from an error response (UI-SPEC backstop row 11); keep
          // hydrating the rest of the index.
          continue;
        }

        const { project } = ProjectDetailResponseSchema.parse(await detailRes.json());
        const lib = await getProjectLibrary();
        // ProjectSnapshotSchema's z.infer (Worker/transport shape — e.g. Opening.width
        // as string|number) and the client's hand-authored ProjectSnapshot interface
        // (entities/estimate/model/types.ts — Opening.width as number) are two distinct
        // types for historical reasons; useSyncStore.ts's flush() bridges the same gap
        // with an identical `as` cast when writing server-merged rooms into local state.
        const next = mergeProjectIntoLibrary(lib, project.data as ProjectSnapshot);
        await saveProjectLibrary(next);
      } catch {
        // Network error or a detail body failing ProjectDetailResponseSchema — skip
        // this one project only, the loop continues with the rest of the index.
        continue;
      }
    }
  } finally {
    useSyncStore.getState().setHydrating(false);
  }
}

/**
 * flush() the local queue, THEN hydrate from the server. Strictly sequential —
 * never `Promise.all`. This ordering is the concrete resolution of this phase's
 * named blast-radius risk (see module header): reading server state down before
 * the local queue has drained could overwrite un-synced local history.
 */
export async function syncAndHydrate(): Promise<void> {
  await useSyncStore.getState().flush();
  // First-sync repair (plan 36-06) runs strictly HERE — after flush() has
  // drained the local queue, strictly before hydrateProjectsFromServer() reads
  // the server down. A pre-Phase-35 stripped server row must never be merged
  // over the intact local snapshot that is its only repair source. A failure
  // inside the repair pass must not prevent hydration from running.
  try {
    await ensureFirstSyncUploaded();
  } catch {
    // Non-fatal — hydration still needs to run so the dashboard reflects
    // server state even if this device's repair pass failed this run.
  }
  await hydrateProjectsFromServer();
}
